/**
 * Admin Templates Page
 * Upload and manage slide template themes
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Upload, RefreshCw, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ThemeUploadArea } from '../../components/admin/ThemeUploadArea';
import { ThemeGallery, Theme } from '../../components/admin/ThemeGallery';
import api from '../../services/api';
import { cn } from '../../lib/utils';

export const Route = createFileRoute('/admin/templates')({
  component: AdminTemplates,
});

function AdminTemplates() {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/themes');
      if (response.data.success) {
        setThemes(response.data.themes || []);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadThemes();
    setRefreshing(false);
  };

  const handleUploadComplete = async () => {
    setShowUpload(false);
    await loadThemes();
  };

  const handleDeleteTheme = async (themeId: string) => {
    if (!confirm(`Are you sure you want to delete theme "${themeId}"? This will remove all layouts and files.`)) {
      return;
    }

    try {
      await api.delete(`/admin/themes/${encodeURIComponent(themeId)}`);
      await loadThemes();
    } catch (error) {
      console.error('Failed to delete theme:', error);
      alert('Failed to delete theme. Please try again.');
    }
  };

  const handleRegeneratePreviews = async (themeId: string) => {
    try {
      await api.post(`/admin/themes/${encodeURIComponent(themeId)}/regenerate-previews`);
      // Reload after a short delay to allow screenshots to generate
      setTimeout(loadThemes, 2000);
    } catch (error) {
      console.error('Failed to regenerate previews:', error);
      alert('Failed to regenerate previews. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Template Management</h1>
          <p className="text-muted-foreground">Upload and manage slide template themes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowUpload(!showUpload)}
            className={showUpload ? "bg-muted" : ""}
          >
            <Upload className="h-4 w-4 mr-2" />
            {showUpload ? 'Hide Upload' : 'Upload Theme'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Themes</CardTitle>
            <Palette className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{themes.length}</div>
            <p className="text-xs text-muted-foreground">Uploaded themes</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Layouts</CardTitle>
            <Palette className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {themes.reduce((sum, t) => sum + t.layouts.length, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Slide templates</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <Palette className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(themes.map(t => t.category)).size}
            </div>
            <p className="text-xs text-muted-foreground">Unique categories</p>
          </CardContent>
        </Card>
      </div>

      {/* Upload Area */}
      {showUpload && (
        <Card>
          <CardHeader>
            <CardTitle>Upload New Theme</CardTitle>
          </CardHeader>
          <CardContent>
            <ThemeUploadArea onUploadComplete={handleUploadComplete} />
          </CardContent>
        </Card>
      )}

      {/* Theme Gallery */}
      <ThemeGallery
        themes={themes}
        onDelete={handleDeleteTheme}
        onRegeneratePreviews={handleRegeneratePreviews}
        onRefresh={handleRefresh}
      />
    </div>
  );
}
